import { useEffect, useState } from 'react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { LayoutDashboard, ListChecks, MapPin, User, LogOut, Menu, X } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { useMe, useLogout } from '@/data/auth';
import { useAuthStore } from '@/stores/auth';
import { cn } from '@/lib/utils';
import { Spinner } from '@/components/ui/spinner';
import { OfflineBanner } from '@/components/OfflineBanner';

const NAV = [
  { to: '/',        label: 'Dashboard',   icon: LayoutDashboard },
  { to: '/logs',    label: 'Tauchgänge',  icon: ListChecks },
  { to: '/sites',   label: 'Tauchplätze', icon: MapPin },
  { to: '/account', label: 'Konto',       icon: User },
] as const;

export function AppShell() {
  const { data: user, isLoading, isError } = useMe();
  const setUser = useAuthStore((s) => s.setUser);
  const logout = useLogout();
  const navigate = useNavigate();
  const { location } = useRouterState();
  const [open, setOpen] = useState(false);

  useEffect(() => { if (user) setUser(user); }, [user, setUser]);
  useEffect(() => { if (isError) void navigate({ to: '/login' }); }, [isError, navigate]);
  // Close drawer on route change.
  useEffect(() => { setOpen(false); }, [location.pathname]);
  // Lock body scroll while drawer is open on mobile.
  useEffect(() => {
    if (open) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  if (isLoading) {
    return (
      <div className="grid min-h-dvh place-items-center text-[var(--muted-foreground)]">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="min-h-dvh bg-[var(--background)]">
      {/* Mobile top bar – hidden on lg+ */}
      <header className="sticky top-0 z-30 flex h-14 items-center justify-between border-b border-ocean-800 bg-ocean-900 px-4 text-ocean-50 lg:hidden">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="-ml-2 rounded-md p-2 transition hover:bg-ocean-800"
          aria-label="Menü öffnen"
        >
          <Menu className="h-5 w-5" />
        </button>
        <Link to="/" className="flex items-center gap-2">
          <Logo className="h-7 w-7" />
          <span className="text-base font-semibold tracking-tight">DiveLogApp</span>
        </Link>
        <button
          type="button"
          onClick={() => logout.mutate()}
          className="-mr-2 rounded-md p-2 transition hover:bg-ocean-800"
          aria-label="Abmelden"
        >
          <LogOut className="h-5 w-5" />
        </button>
      </header>

      {/* Backdrop for mobile drawer */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-ocean-950/60 backdrop-blur-sm lg:hidden"
          aria-hidden
        />
      )}

      {/* Sidebar – drawer on mobile, persistent on lg+ */}
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-50 flex w-60 flex-col bg-ocean-900 text-ocean-50 shadow-2xl transition-transform duration-200 lg:shadow-none',
          open ? 'translate-x-0' : '-translate-x-full',
          'lg:translate-x-0',
        )}
      >
        <div className="flex h-16 items-center justify-between border-b border-ocean-800 px-5">
          <Link to="/" className="flex items-center gap-3">
            <Logo className="h-8 w-8" />
            <span className="text-lg font-semibold tracking-tight">DiveLogApp</span>
          </Link>
          <button
            type="button"
            onClick={() => setOpen(false)}
            className="rounded-md p-1.5 transition hover:bg-ocean-800 lg:hidden"
            aria-label="Menü schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <nav className="flex flex-1 flex-col gap-1 p-3">
          {NAV.map(({ to, label, icon: Icon }) => {
            const active = location.pathname === to || (to !== '/' && location.pathname.startsWith(to));
            return (
              <Link
                key={to}
                to={to}
                className={cn(
                  'flex items-center gap-3 rounded-md px-3 py-2 text-sm transition',
                  active ? 'bg-ocean-700 text-white' : 'text-ocean-100 hover:bg-ocean-800',
                )}
              >
                <Icon className="h-4 w-4" />
                <span>{label}</span>
              </Link>
            );
          })}
        </nav>

        <div className="border-t border-ocean-800 p-3">
          {user && (
            <div className="mb-2 px-3 py-1.5 text-xs">
              <p className="font-medium text-white">{user.firstname} {user.lastname}</p>
              <p className="truncate text-ocean-200">{user.email}</p>
            </div>
          )}
          <button
            type="button"
            className="flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-ocean-100 transition hover:bg-ocean-800"
            onClick={() => logout.mutate()}
          >
            <LogOut className="h-4 w-4" />
            <span>Abmelden</span>
          </button>
        </div>
      </aside>

      {/* Main content – left-offset for the persistent sidebar on lg+ */}
      <main className="lg:pl-60">
        <OfflineBanner />
        <Outlet />
      </main>
    </div>
  );
}
